import { useRef, type ReactNode } from 'react'
import { Float } from '@headlessui-float/react'
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ParseKeys } from 'i18next'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { useHeadlessUiAnchor } from 'shared/feature-flags/use-headless-ui-anchor'
import ArrowDown from 'shared/icons/arrow-down-icon'
import CheckIcon from 'shared/icons/check-icon'
import CloseIcon from 'shared/icons/close-icon'
import { Loader } from './loader'

export interface OptionsInterface {
  id: string | number
  caption: string
}

export interface DataInterface<T extends OptionsInterface> {
  id: T['id']
  caption: string
}

export interface FormSelectProps<T extends OptionsInterface> {
  label?: string
  data?: T[]
  onChange: (data: DataInterface<T>['id']) => void
  value?: DataInterface<T>['id']
  placeholder?: ParseKeys
  error?: string
  required?: boolean
  className?: string
  withoutCloseIcon?: boolean
}

function FormSelect<T extends OptionsInterface>({
  label,
  data,
  onChange,
  value,
  placeholder = 'global.select',
  error,
  required,
  withoutCloseIcon = false,
  className,
}: FormSelectProps<T>): ReactNode {
  const { t } = useTranslation()
  const useNativeAnchor = useHeadlessUiAnchor()
  const caption = data?.find(el => el.id === value)?.caption
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  return (
    <div className={twMerge(`relative flex flex-col gap-1`, className)}>
      <Listbox value={value} onChange={onChange}>
        {({ open }) => {
          const trigger = (
            <div className="flex flex-col gap-1">
              {(label || required) && (
                <Label className="flex gap-0.5 text-sm font-medium text-text-primary">
                  {label && <span>{label}</span>}
                  {required && <span className="text-red">*</span>}
                </Label>
              )}
              <ListboxButton
                className={twMerge(
                  'relative flex w-full cursor-default items-center justify-between rounded-lg border bg-background-secondary px-4 py-2.5 text-left text-sm text-text-primary main-transition-colors',
                  open ? 'border-brand-primary' : 'border-text-secondary/30',
                  open && 'border-brand-primary',
                  'focus:outline-none focus-visible:border-brand-primary',
                )}
                as={'button'}
                ref={buttonRef}
              >
                <>
                  {value && data ? caption : t(placeholder)}
                  <div className="ml-5 flex items-center gap-1">
                    {value && !withoutCloseIcon && (
                      <CloseIcon
                        className={`cursor-pointer fill-text-primary`}
                        onClick={() => onChange(undefined!)}
                      />
                    )}
                    <ArrowDown
                      className={twMerge(
                        'stroke-text-primary transition-transform duration-300',
                        open && 'rotate-180',
                      )}
                    />
                  </div>
                </>
              </ListboxButton>
            </div>
          )

          const options = (
            <ListboxOptions
              {...(useNativeAnchor
                ? { portal: true, anchor: { to: 'bottom end' as const, gap: 10 } }
                : {})}
              className={twMerge(
                'max-h-60 w-full border-collapse overflow-auto rounded-md bg-background-secondary text-sm focus:outline-none',
                useNativeAnchor && 'z-50 w-(--button-width)',
              )}
            >
              <div ref={dropdownRef}>
                {data === undefined ? (
                  <div className="flex justify-center rounded-md border border-t-0 border-text-secondary/30 px-4 py-1">
                    <Loader className="scale-75" />
                  </div>
                ) : (
                  data.map(el => {
                    return (
                      <ListboxOption
                        key={el.id}
                        value={el.id}
                        className={({ focus }) =>
                          twMerge(
                            'group flex items-center justify-between border border-t-0 border-text-secondary/30 px-4 py-2.5 first:rounded-t-md first:border-t last:rounded-b-md hover:bg-brand-primary hover:text-background-secondary',
                            focus
                              ? 'bg-brand-primary text-background-secondary'
                              : 'bg-background-secondary text-text-primary',
                          )
                        }
                      >
                        {({ focus }) => (
                          <div className="flex w-full items-center justify-between gap-3">
                            {el.caption}
                            {el.id === value && (
                              <CheckIcon
                                className={twMerge(
                                  'size-4 group-hover:stroke-background-secondary',
                                  focus ? 'stroke-background-secondary' : 'stroke-brand-primary',
                                )}
                                aria-hidden="true"
                              />
                            )}
                          </div>
                        )}
                      </ListboxOption>
                    )
                  })
                )}
              </div>
            </ListboxOptions>
          )

          if (useNativeAnchor) {
            return (
              <>
                {trigger}
                {options}
              </>
            )
          }

          return (
            <Float
              offset={10}
              portal
              onUpdate={() => {
                if (dropdownRef.current && buttonRef.current) {
                  const buttonWidth = buttonRef.current.getBoundingClientRect().width
                  dropdownRef.current.style.width = `${buttonWidth}px`
                }
              }}
              flip
              placement="bottom-end"
              as="div"
              enter="transition duration-100 ease-out"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition duration-75 ease-in"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              {trigger}
              {options}
            </Float>
          )
        }}
      </Listbox>
      {error && <p className="text-sm text-danger">{error}</p>}
    </div>
  )
}

export default FormSelect
